<template>
    <div class="items-center justify-center w-3/4 px-8 py-6 mt-4 bg-white bg-opacity-50 rounded-lg sm:px-6">
        <div class="flex justify-center w-full mb-8">
            <h3 class="text-2xl font-medium">
                {{ surgeon.name }} Similarity Report
            </h3>
        </div>
        <div class="relative">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center">
                <button @click="openPaymentModal()"
                type="button" class="inline-flex items-center gap-x-1.5 rounded-full bg-green-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-green-700">
                <svg class="-ml-1 -mr-0.5 h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                </svg>
                {{ buttonText }}
                </button>
            </div>
        </div>

        <div class="flex items-center w-full mt-8">
            <div v-if="isLoading" class="absolute top-0 bottom-0 left-0 right-0 flex flex-col items-center justify-start w-full h-screen overflow-hidden bg-gray-900 z-100 bg-opacity-30">
                <div class="w-10 h-10 mt-20 ease-linear border-4 border-t-4 border-gray-200 border-top-rounded-full loader"></div>
            </div>
            <div v-if="!isLoading && reportRun" class="w-full">
                <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2">
                    <li
                        v-for="surgeon in similarityResults"
                        :key="surgeon.surgeon_profile.id"
                        class="flex flex-col w-full col-span-1 text-center bg-white rounded-lg shadow"
                    >
                        <div class="flex grid w-full grid-cols-2 gap-2 px-10">
                            <div class="flex justify-center col-span-1 pt-8">
                                <img class="flex-shrink-0 w-32 h-32 rounded-full" :src="surgeon.surgeon_picture" alt="">
                            </div>
                            <div class="justify-center col-span-1 px-4">
                                <h3 class="mt-6 text-lg font-medium text-gray-900">
                                    <a class="tw-link" :href="route('surgeons.show', {'surgeon' : surgeon.surgeon_profile.id })">{{ surgeon.similarity_data.full_name }}</a>
                                </h3>
                                <dl class="flex flex-col justify-between flex-grow mt-1">
                                    <dt class="sr-only">Specialty</dt>
                                    <dd class="text-gray-500 text-md">{{ surgeon.surgeon_profile.primary_specialty }}</dd>
                                    <dt class="sr-only">Location</dt>
                                    <dd class="text-gray-500 text-md">
                                        {{ surgeon.surgeon_profile.primary_practice_city }}, {{ surgeon.surgeon_profile.primary_practice_state }}
                                    </dd>
                                </dl>
                                <dl class="flex flex-col justify-between flex-grow py-4 mt-4 rounded-lg">
                                    <dt class="text-lg font-medium text-gray-900">
                                        MTI Similarity Index Rating
                                    </dt>
                                    <dd class="mt-1 text-xl"
                                        :class="{
                                            'text-indigo-600' : surgeon.similarity_data.score >= 50,
                                            'text-blue-600' : surgeon.similarity_data.score < 50 && surgeon.similarity_data.score >= 40,
                                            'text-green-600' : surgeon.similarity_data.score < 40 && surgeon.similarity_data.score >= 30,
                                            'text-yellow-600' : surgeon.similarity_data.score < 30 && surgeon.similarity_data.score >= 20,
                                            'text-gray-600' : surgeon.similarity_data.score < 20,
                                        }">
                                        {{ mtiSimiliarityIndex(surgeon.similarity_data.score) }}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                        <div class="mt-1">
                            <div id="chart" class="flex w-full">
                                <apex-chart type="radar" height='400' width='500' class="justify-end"
                                :options="{
                                    chart: {
                                        toolbar: {
                                            show: false,
                                        }
                                    },
                                    xaxis: {
                                        categories: surgeon.similarity_graph_data.labels
                                    },
                                }"
                                :series="[{
                                    name: 'Similarity',
                                    data: surgeon.similarity_graph_data.values
                                }]"
                                ></apex-chart>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <tw-modal ref="payment_modal">
            <template v-slot:content>
            <div class="w-full px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                <div class="w-full sm:flex sm:items-start">
                    <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-green-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                        <!-- Heroicon name: outline/exclamation -->
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-green-600">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 class="text-lg leading-6 text-gray-600" id="modal-title">
                            Purchase <select
                            name="surgeonCount"
                            id="surgeon-count"
                            @change="getPrice"
                            class="py-2 pl-3 mt-1 mb-4 text-base border-gray-300 rounded-md pr-7 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                            >
                            <option selected value="5">5</option>
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="20">20</option>
                            <option value="25">25</option>
                            <option value="30">30</option>
                            <option value="35">35</option>
                            <option value="40">40</option>
                            <option value="45">45</option>
                            <option value="50">50</option>
                            </select> more results for {{ moneyToString(price) }}
                        </h3>
                    </div>
                </div>
                <div v-if = "paymentMethod" class="px-6 py-5 rounded-md bg-gray-50 sm:flex sm:items-start sm:justify-between">
                    <h4 class="sr-only">{{ paymentMethod.brand }}</h4>
                    <div class="sm:flex sm:items-start">
                        <svg class="w-auto h-8 sm:h-6 sm:flex-shrink-0" viewBox="0 0 36 24" aria-hidden="true">
                            <rect width="36" height="24" fill="#224DBA" rx="4" />
                            <path fill="#fff" d="M10.925 15.673H8.874l-1.538-6c-.073-.276-.228-.52-.456-.635A6.575 6.575 0 005 8.403v-.231h3.304c.456 0 .798.347.855.75l.798 4.328 2.05-5.078h1.994l-3.076 7.5zm4.216 0h-1.937L14.8 8.172h1.937l-1.595 7.5zm4.101-5.422c.057-.404.399-.635.798-.635a3.54 3.54 0 011.88.346l.342-1.615A4.808 4.808 0 0020.496 8c-1.88 0-3.248 1.039-3.248 2.481 0 1.097.969 1.673 1.653 2.02.74.346 1.025.577.968.923 0 .519-.57.75-1.139.75a4.795 4.795 0 01-1.994-.462l-.342 1.616a5.48 5.48 0 002.108.404c2.108.057 3.418-.981 3.418-2.539 0-1.962-2.678-2.077-2.678-2.942zm9.457 5.422L27.16 8.172h-1.652a.858.858 0 00-.798.577l-2.848 6.924h1.994l.398-1.096h2.45l.228 1.096h1.766zm-2.905-5.482l.57 2.827h-1.596l1.026-2.827z" />
                        </svg>
                        <div class="mt-3 sm:mt-0 sm:ml-4">
                            <div class="text-sm font-medium text-gray-900">Ending with {{ paymentMethod.last4 }}</div>
                            <div class="mt-1 text-sm text-gray-600 sm:flex sm:items-center">
                                <div>Expires {{ paymentMethod.exp_month }} / {{ paymentMethod.exp_year}}</div>
                                <span class="hidden sm:mx-2 sm:inline" aria-hidden="true"></span>
                            </div>
                        </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:ml-6 sm:flex-shrink-0">
                        <a :href="route('billing.payment_method')" class="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Edit</a>
                    </div>
                </div>
                <div v-else>
                    <div class="p-6 text-center rounded-lg bg-gray-50">
                        <svg class="w-12 h-12 mx-auto text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                        </svg>

                        <h3 class="mt-2 text-sm font-semibold text-gray-900">No Payment Method</h3>
                        <p class="mt-1 text-sm text-gray-500">Get started by adding a new payment Method.</p>
                        <div class="mt-6">
                            <a type="button" :href="route('billing.payment_method', {'return_route' : '/surgeons/'+surgeon.id+'/similarity/'})" class="inline-flex items-center px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                            <svg class="-ml-0.5 mr-1.5 h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                            </svg>
                            Add Card
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
                <button class="tw-button-gray" @click="closePaymentModal">
                    Cancel
                </button>
                <button v-if="paymentMethod" class="tw-button-green" @click="purchase()">
                    Confirm
                </button>
            </div>
        </template>
        </tw-modal>
    </div>
</template>
<script>
import axios from 'axios';
import VueApexCharts from 'vue-apexcharts';

export default {
    props: {
        surgeon: {
            type: [Array, Object],
            required: true,
        },
        isSubscribed: {
            type: Boolean,
            required: true,
        },
        isPurchased: {
            type: Boolean,
            required: true
        },
        originalPurchasedCount: {
            type: Number,
            required: true,
        },
        token: {
            type: String,
            required: true,
        },
        paymentMethod: {
            type: [Array, Object],
            required: false,
        },
        plan: {
            type: [Array, Object],
            required: true,
        },
        openModal: {
            type: Boolean,
            required: false,
        }
    },
    data() {
        return {
            surgeonCount: this.originalPurchasedCount,
            purchasedCount: this.originalPurchasedCount,
            price: 5,
            buttonText: "Get More Results",
            free: this.isSubscribed,
            isLoading: false,
            reportRun: false,
            moreCount: 5,
            similarityResults: {},
        };
    },
    mounted() {
        if (this.originalPurchasedCount > 0) {
            this.runSimilarityReport( false );
        } else {
            this.surgeonCount = 5;
        }

        if (!this.isSubscribed) {
            this.surgeonCount = 0;
            this.price = this.price * 10;
        }

        if (this.openModal ) {
            this.openPaymentModal()
        }
    },
    methods: {
        getPrice( count ) {
            const selectedTotal = document.getElementById( 'surgeon-count' );

            this.moreCount = parseInt(selectedTotal.value);

            if (this.free) {
                this.price = this.moreCount;
            }
            else {
                this.free = false;
                this.price = this.moreCount * 10
            }
        },
        async runSimilarityReport( increment ) {
            try {
                if ( increment ) {
                    this.surgeonCount = this.surgeonCount + this.moreCount;
                    this.purchasedCount = this.surgeonCount
                }
                this.isLoading = true;
                const searchRoute = this.route('surgeons.similarity-report-data', { surgeon: this.surgeon.id, length: this.surgeonCount});
                const response = await axios.get(searchRoute);
                this.similarityResults = response.data;
                this.isLoading = false;
                this.reportRun = true;
            }
            catch (error) {
                this.similarityResults = {};
                this.isLoading = false;
            }
        },
        async purchase() {
            try {
                axios.post(this.route('billing.new_subscription'),
                {
                    'plan' : this.plan.identifier,
                    'stripe_id' : this.plan.stripe_id,
                    'token' : this.token,
                    'quantity' : this.surgeonCount / 5,
                    'price' : this.price * 100
                }
                )
                this.$refs.payment_modal.hide();
            } catch (error) {
                this.isLoading = false;
                console.log(error)
                return
            }

            this.runSimilarityReport(true)
        },

        openPaymentModal() {
            this.$refs.payment_modal.show();
        },
        moneyToString(num) {
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            });
            return formatter.format(parseFloat(num));
        },
        buttonTextChange( e ) {
            let count = 5;
            if ( e ) {
                count = e.target.value - 5;
            }

            this.getButtonText(parseInt(count));
        },
        mtiSimiliarityIndex(num) {
           if (num >= 50) {
            return 'Super'
           }
           else if (num < 50 && num >= 40) {
            return 'Excellent'
           }
           else if (num < 40 && num >= 20) {
            return 'Good'
           }
           else if (num < 20 && num >= 10) {
            return 'Fair'
           }
           else {
            return 'Mild'
           }
        },
        closePaymentModal(){
            this.$refs.payment_modal.hide();
        }
    },
    components: { VueApexCharts }
}
</script>
